import { useEffect, useState } from 'react';
import axios from 'axios';
import { TrendingUp, TrendingDown, Minus, Calendar } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RANGES = [
  { value: 7, label: '7d' },
  { value: 30, label: '30d' },
  { value: 90, label: '90d' },
];

/**
 * Full-size analytics modal opened by clicking a sparkline. Shows the daily
 * matches chart, week-over-week trend, and totals across selectable ranges.
 */
const SavedViewAnalyticsModal = ({ view, onClose }) => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!view) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const { data: body } = await axios.get(
          `${API_URL}/api/saved-views/${view.id}/analytics?days=${days}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!cancelled) setData(body);
      } catch {
        if (!cancelled) setData(null);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [view, days]);

  if (!view) return null;

  const buckets = data?.buckets || [];
  const max = Math.max(1, ...buckets.map((b) => b.count));
  const trend = data?.trend;

  const TrendIcon = trend?.direction === 'up' ? TrendingUp
    : trend?.direction === 'down' ? TrendingDown : Minus;
  const trendColor = trend?.direction === 'up' ? 'text-emerald-600'
    : trend?.direction === 'down' ? 'text-red-500' : 'text-muted-foreground';

  return (
    <Dialog open={Boolean(view)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="saved-view-analytics-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-[#111565]" />
            {view.name}
          </DialogTitle>
          <DialogDescription>
            <code className="text-xs">{view.path}{view.query_string ? `?${view.query_string}` : ''}</code>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Range tabs */}
          <div className="flex items-center justify-between">
            <div className="flex gap-1 rounded-md border p-1 bg-muted/40">
              {RANGES.map((r) => (
                <Button
                  key={r.value}
                  size="sm"
                  variant={days === r.value ? 'default' : 'ghost'}
                  className={`h-7 px-3 text-xs ${days === r.value ? 'bg-[#111565]' : ''}`}
                  onClick={() => setDays(r.value)}
                  data-testid={`analytics-range-${r.value}`}
                >
                  {r.label}
                </Button>
              ))}
            </div>
            {trend && (
              <div className={`inline-flex items-center gap-1 text-sm font-semibold ${trendColor}`}
                   data-testid="analytics-trend">
                <TrendIcon className="h-4 w-4" />
                {trend.direction !== 'flat' ? `${Math.abs(trend.delta_pct)}%` : 'flat'}
                <span className="text-muted-foreground text-xs font-normal">vs prior 7d</span>
              </div>
            )}
          </div>

          {/* Totals */}
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-md border p-3">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Total in window</p>
              <p className="text-2xl font-bold text-[#111565] mt-1">{data?.total ?? '—'}</p>
            </div>
            <div className="rounded-md border p-3">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Last 7 days</p>
              <p className="text-2xl font-bold text-[#111565] mt-1">{trend?.recent ?? '—'}</p>
            </div>
            <div className="rounded-md border p-3">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Prior 7 days</p>
              <p className="text-2xl font-bold text-muted-foreground mt-1">{trend?.prior ?? '—'}</p>
            </div>
          </div>

          {/* Bar chart */}
          <div className="rounded-md border p-3">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-2">
              Daily matches (created in window)
            </p>
            {loading ? (
              <div className="h-32 bg-muted/40 rounded animate-pulse" />
            ) : buckets.length === 0 ? (
              <p className="text-xs text-muted-foreground py-4 text-center">No data.</p>
            ) : (
              <div className="flex items-end gap-0.5 h-32" data-testid="analytics-chart">
                {buckets.map((b) => (
                  <div
                    key={b.date}
                    className="flex-1 bg-[#111565]/20 hover:bg-[#111565]/40 rounded-sm transition cursor-default"
                    style={{ height: `${(b.count / max) * 100}%`, minHeight: '2px' }}
                    title={`${b.date}: ${b.count}`}
                  />
                ))}
              </div>
            )}
            <div className="flex justify-between text-[10px] text-muted-foreground mt-1.5">
              <span>{buckets[0]?.date}</span>
              <span>{buckets[buckets.length - 1]?.date}</span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SavedViewAnalyticsModal;
